import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { RetrospectivaData, ApiResponse, ESTADO_LABELS } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export const RetrospectivaPanel: React.FC = () => {
    const [dias, setDias] = useState<number>(14);
    const [data, setData] = useState<RetrospectivaData | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const hasta = new Date().toISOString().split('T')[0];
            const desdeDate = new Date(Date.now() - dias * 86400000);
            const desde = desdeDate.toISOString().split('T')[0];
            const res = await apiFetch(`${API_BASE}/tareas/retrospective?desde=${desde}&hasta=${hasta}`);
            const json: ApiResponse<RetrospectivaData> = await res.json();
            if (json.success && json.data) setData(json.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [dias]);

    if (!data) {
        return <div className="text-center text-slate-400 py-8">{loading ? 'Cargando...' : 'Sin datos'}</div>;
    }

    const bloqueosOrdenados = Object.entries(data.bloqueos_frecuentes_por_columna)
        .sort(([, a], [, b]) => (b as number) - (a as number));

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">🔄 Retrospectiva</h3>
                    <p className="text-xs text-slate-500">
                        Período: <span className="font-medium">{data.desde}</span> → <span className="font-medium">{data.hasta}</span>
                    </p>
                </div>
                <div className="flex gap-1">
                    {[7, 14, 30].map(d => (
                        <button
                            key={d}
                            onClick={() => setDias(d)}
                            className={`px-3 py-1 text-xs rounded ${
                                dias === d
                                    ? 'bg-slate-800 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <MetricCard label="Completadas" value={data.completadas} color="emerald" />
                <MetricCard
                    label="Cycle Time prom."
                    value={data.cycle_time_promedio_dias !== null ? `${data.cycle_time_promedio_dias}d` : '—'}
                    color="blue"
                />
                <MetricCard label="Throughput/sem" value={data.throughput_semanal} color="purple" />
                <MetricCard label="Bloqueos totales" value={data.bloqueos_totales} color="red" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        🚧 Bloqueos por columna
                    </h4>
                    {bloqueosOrdenados.length === 0 ? (
                        <div className="text-xs text-slate-400 italic">— Sin bloqueos</div>
                    ) : (
                        <div className="space-y-1">
                            {bloqueosOrdenados.map(([col, total]) => (
                                <div key={col} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">{ESTADO_LABELS[col as keyof typeof ESTADO_LABELS] || col}</span>
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">{total as number}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        🐌 Items más largos del período
                    </h4>
                    {data.items_mas_largos.length === 0 ? (
                        <div className="text-xs text-slate-400 italic">— Sin datos</div>
                    ) : (
                        <div className="space-y-1">
                            {data.items_mas_largos.map(i => (
                                <div key={i.id} className="flex items-baseline gap-2 text-sm">
                                    <span className="font-mono text-xs text-slate-500 w-8">#{i.id}</span>
                                    <span className="text-slate-800 truncate flex-1">{i.titulo}</span>
                                    <span className="text-xs text-slate-500">{i.dias}d</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {data.items_archivados > 0 && (
                <div className="mt-4 p-2 bg-slate-50 border border-slate-200 rounded text-sm text-slate-700">
                    📦 <strong>{data.items_archivados}</strong> tarea(s) archivada(s) en el período.
                </div>
            )}
        </div>
    );
};

const MetricCard: React.FC<{ label: string; value: string | number; color: 'emerald' | 'blue' | 'purple' | 'red' }> = ({ label, value, color }) => {
    const colors = {
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
        blue: 'bg-blue-50 border-blue-200 text-blue-800',
        purple: 'bg-purple-50 border-purple-200 text-purple-800',
        red: 'bg-red-50 border-red-200 text-red-800',
    };
    return (
        <div className={`p-3 rounded-lg border ${colors[color]}`}>
            <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-2xl font-bold mt-1">{value}</div>
        </div>
    );
};
